/**
 * Voters — the one set of rules a voter record must satisfy.
 *
 * Three consumers read this file and nothing else: the Add voter panel's yup schema, the import
 * review's `validateVoterRecord`, and the downloadable template (its columns, its Required flags and
 * its sample rows, which a test proves validate). Before this existed the three disagreed — the
 * template had no email column while the server required one, so every templated row 400'd.
 *
 * Two rule sets, because two backends are live at once:
 *
 *  - `baseline` — the api.command build that predates the voters workspace. It still REQUIRES email
 *    and VIN, and silently drops gender / date of birth, so those columns are not offered.
 *  - `v2` — the build that announces `create-v2` on `GET Voters/capabilities`. Email and VIN are
 *    optional; gender and date of birth exist.
 *
 * Pure: no React, no network. `today` is always passed in, so the 18th-birthday boundary is
 * testable to the day.
 */

import * as yup from 'yup';

import { parseNgPhone } from '@/utils/phone-ng';

import { normalizePuCode } from './pu-code';

export type VoterRulesMode = 'baseline' | 'v2';

export type VoterField =
	| 'firstName'
	| 'middleName'
	| 'lastName'
	| 'phoneNumber'
	| 'email'
	| 'voterRegistrationNumber'
	| 'gender'
	| 'dateOfBirth'
	| 'pollingUnitCode';

export const VOTER_MIN_AGE = 18;
export const VOTER_MAX_AGE = 120;
export const NAME_MAX_LENGTH = 100;

export interface IVoterFieldRule {
	field: VoterField;
	label: string;
	/** Which backends accept this column at all. */
	modes: readonly VoterRulesMode[];
	required: Record<VoterRulesMode, boolean>;
	/** Template instructions — one plain sentence. */
	description: string;
	/** Lower-cased header spellings recognised on import, beyond the field key itself. */
	synonyms: readonly string[];
}

const BOTH: readonly VoterRulesMode[] = ['baseline', 'v2'];

export const VOTER_FIELD_RULES: readonly IVoterFieldRule[] = [
	{
		field: 'firstName',
		label: 'First name',
		modes: BOTH,
		required: { baseline: true, v2: true },
		description: "Voter's first name.",
		synonyms: ['first_name', 'firstname', 'fname', 'first name', 'given name', 'forename'],
	},
	{
		field: 'middleName',
		label: 'Middle name',
		modes: BOTH,
		required: { baseline: false, v2: false },
		description: 'Optional. Other names.',
		synonyms: ['middle_name', 'middlename', 'middle name', 'other names', 'other_names', 'othernames', 'mname'],
	},
	{
		field: 'lastName',
		label: 'Last name',
		modes: BOTH,
		required: { baseline: true, v2: true },
		description: "Voter's surname.",
		synonyms: ['last_name', 'lastname', 'lname_voter', 'surname', 'last name', 'family name'],
	},
	{
		field: 'phoneNumber',
		label: 'Phone number',
		modes: BOTH,
		required: { baseline: true, v2: true },
		description: 'Nigerian mobile. 0803…, 803… and +234803… all work — it is saved as 11 digits.',
		synonyms: ['phone_number', 'phonenumber', 'phone', 'mobile', 'mobile_number', 'phone number', 'gsm', 'telephone'],
	},
	{
		field: 'email',
		label: 'Email',
		modes: BOTH,
		required: { baseline: true, v2: false },
		description: 'Email address.',
		synonyms: ['email_address', 'emailaddress', 'e-mail', 'email address', 'mail'],
	},
	{
		field: 'voterRegistrationNumber',
		label: 'VIN',
		modes: BOTH,
		required: { baseline: true, v2: false },
		description: 'Voter identification number from the PVC.',
		synonyms: ['vin', 'voter_registration_number', 'voterregistrationnumber', 'voter id', 'voter_id', 'pvc', 'pvc number'],
	},
	{
		field: 'gender',
		label: 'Gender',
		modes: ['v2'],
		required: { baseline: false, v2: false },
		description: 'Optional. Male or Female.',
		synonyms: ['sex'],
	},
	{
		field: 'dateOfBirth',
		label: 'Date of birth',
		modes: ['v2'],
		required: { baseline: false, v2: false },
		description: 'Optional. YYYY-MM-DD or DD/MM/YYYY. The voter must be at least 18.',
		synonyms: ['dob', 'date_of_birth', 'dateofbirth', 'date of birth', 'birth date', 'birthdate', 'birthday'],
	},
	{
		field: 'pollingUnitCode',
		label: 'Polling unit code',
		modes: BOTH,
		required: { baseline: true, v2: true },
		description: 'INEC polling-unit code, like 04-09-02-011.',
		synonyms: ['polling_unit_code', 'pollingunitcode', 'pu_code', 'pu code', 'pucode', 'delim', 'polling unit code', 'pu'],
	},
];

export const fieldsFor = (mode: VoterRulesMode): IVoterFieldRule[] => VOTER_FIELD_RULES.filter((r) => r.modes.includes(mode));

export const isFieldRequired = (field: VoterField, mode: VoterRulesMode): boolean =>
	VOTER_FIELD_RULES.find((r) => r.field === field)?.required[mode] ?? false;

export const isFieldOffered = (field: VoterField, mode: VoterRulesMode): boolean =>
	VOTER_FIELD_RULES.find((r) => r.field === field)?.modes.includes(mode) ?? false;

export const ruleFor = (field: VoterField): IVoterFieldRule => VOTER_FIELD_RULES.find((r) => r.field === field)!;

// ── Reason codes ─────────────────────────────────────────────────────────────────────────────
// The same vocabulary the server uses for import failures, so a client-side rejection and a
// server-side one read identically in "rows to fix".

export type VoterReasonCode =
	| 'MISSING_FIRST_NAME'
	| 'MISSING_LAST_NAME'
	| 'FIELD_TOO_LONG'
	| 'MISSING_PHONE'
	| 'INVALID_PHONE'
	| 'MISSING_EMAIL'
	| 'INVALID_EMAIL'
	| 'MISSING_VIN'
	| 'INVALID_VIN'
	| 'INVALID_GENDER'
	| 'INVALID_DATE_OF_BIRTH'
	| 'UNDER_18'
	| 'MISSING_POLLING_UNIT'
	| 'INVALID_POLLING_UNIT_FORMAT';

export interface IVoterIssue {
	field: VoterField;
	code: VoterReasonCode;
	message: string;
}

// ── Normalisers ──────────────────────────────────────────────────────────────────────────────

export type VoterGender = 'Male' | 'Female';

/** '' for blank; `null` for something that is not a gender we store. */
export const normalizeGender = (raw: unknown): VoterGender | '' | null => {
	const value = String(raw ?? '')
		.trim()
		.toLowerCase();
	if (!value) return '';
	if (value === 'm' || value === 'male') return 'Male';
	if (value === 'f' || value === 'female') return 'Female';
	return null;
};

const isRealDate = (y: number, m: number, d: number): boolean => {
	if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return false;
	if (y < 1850 || y > 2200 || m < 1 || m > 12 || d < 1) return false;
	const probe = new Date(Date.UTC(y, m - 1, d));
	return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
};

const iso = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** Excel's day 1 is 1900-01-01, with the 1900 leap-year bug: serial 60 does not exist. */
const fromExcelSerial = (serial: number): string | null => {
	if (!Number.isFinite(serial) || serial < 1 || serial > 80000) return null;
	const ms = Date.UTC(1899, 11, 30) + Math.floor(serial) * 86_400_000;
	const date = new Date(ms);
	return iso(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

/**
 * Any date of birth a person or a spreadsheet writes → `YYYY-MM-DD`.
 *
 * Accepts ISO (`1990-04-17`), Nigerian day-first (`17/04/1990`, `17-04-1990`, `17.04.1990`) and an
 * Excel date serial (`32980`). Day-first is the ONLY slash order accepted: `04/05/1990` is 4 May,
 * never April 5 — this is the convention every Nigerian form uses. '' for blank; `null` for
 * unreadable.
 */
export const normalizeDob = (raw: unknown): string | '' | null => {
	if (raw instanceof Date) {
		if (Number.isNaN(raw.getTime())) return null;
		return iso(raw.getFullYear(), raw.getMonth() + 1, raw.getDate());
	}
	if (typeof raw === 'number') return fromExcelSerial(raw);
	const value = String(raw ?? '').trim();
	if (!value) return '';

	let match = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T\s].*)?$/.exec(value);
	if (match) {
		const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
		return isRealDate(y, m, d) ? iso(y, m, d) : null;
	}
	match = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(value);
	if (match) {
		const [d, m, y] = [Number(match[1]), Number(match[2]), Number(match[3])];
		return isRealDate(y, m, d) ? iso(y, m, d) : null;
	}
	if (/^\d{4,5}(\.\d+)?$/.test(value)) return fromExcelSerial(Number(value));
	return null;
};

/** Today's calendar date in Lagos (UTC+1, no DST) — the server's clock for the 18th birthday. */
export const lagosToday = (now: Date = new Date()): string => {
	const shifted = new Date(now.getTime() + 60 * 60 * 1000);
	return iso(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
};

/** Whole years between an ISO birth date and an ISO "today". The birthday itself counts. */
export const ageOn = (dobIso: string, todayIso: string): number => {
	const [by, bm, bd] = dobIso.split('-').map(Number);
	const [ty, tm, td] = todayIso.split('-').map(Number);
	let age = ty - by;
	if (tm < bm || (tm === bm && td < bd)) age -= 1;
	return age;
};

/** The latest date of birth that is old enough today — the date input's `max`. */
export const latestEligibleDob = (todayIso: string): string => {
	const [y, m, d] = todayIso.split('-').map(Number);
	// 29 Feb birthdays: someone born 2008-02-29 turns 18 on 2026-03-01, so clamp to a real date.
	const year = y - VOTER_MIN_AGE;
	return isRealDate(year, m, d) ? iso(year, m, d) : iso(year, m, d - 1);
};

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Letters and digits only, upper-cased; spaces and dashes a PVC photocopy adds are dropped. */
export const normalizeVin = (raw: unknown): string =>
	String(raw ?? '')
		.replace(/[\s-]+/g, '')
		.toUpperCase();

const VIN_SHAPE = /^[A-Z0-9]{8,32}$/;

// ── Validation ───────────────────────────────────────────────────────────────────────────────

export interface IVoterInput {
	firstName?: unknown;
	middleName?: unknown;
	lastName?: unknown;
	phoneNumber?: unknown;
	email?: unknown;
	voterRegistrationNumber?: unknown;
	gender?: unknown;
	dateOfBirth?: unknown;
	pollingUnitCode?: unknown;
}

const text = (v: unknown): string => (v === null || v === undefined ? '' : String(v)).trim();

export const VOTER_MESSAGES = {
	firstNameMissing: 'Enter a first name.',
	lastNameMissing: 'Enter a last name.',
	nameTooLong: `Keep names under ${NAME_MAX_LENGTH} characters.`,
	phoneMissing: 'Enter a phone number.',
	phoneInvalid: 'Enter an 11-digit Nigerian number, like 0803 111 2233.',
	emailMissing: 'Enter an email address — this server still needs one.',
	emailInvalid: 'Enter an email like ada@example.com, or leave it blank.',
	vinMissing: 'Enter the VIN — this server still needs one.',
	vinInvalid: 'A VIN is letters and digits only, like 90F5A1B2C3D4E5F6789.',
	genderInvalid: 'Use Male or Female, or leave it blank.',
	dobInvalid: 'Use a real date, like 1990-04-17 or 17/04/1990.',
	underAge: `Voters must be at least ${VOTER_MIN_AGE}.`,
	dobTooOld: 'Check the year — that date is more than 120 years ago.',
	puMissing: 'Choose the polling unit, or paste its code.',
} as const;

/**
 * Every rule a record breaks, in field order. The panel shows the first per field; the import review
 * joins them into the "What to fix" column.
 */
export interface IValidateOptions {
	/** Edit: the server keeps the stored number when `phoneNumber` is blank, so blank is allowed. */
	phoneOptional?: boolean;
}

export const validateVoterInput = (
	input: IVoterInput,
	mode: VoterRulesMode,
	todayIso: string = lagosToday(),
	options: IValidateOptions = {}
): IVoterIssue[] => {
	const issues: IVoterIssue[] = [];
	const add = (field: VoterField, code: VoterReasonCode, message: string) => issues.push({ field, code, message });

	const first = text(input.firstName);
	const middle = text(input.middleName);
	const last = text(input.lastName);
	if (!first) add('firstName', 'MISSING_FIRST_NAME', VOTER_MESSAGES.firstNameMissing);
	else if (first.length > NAME_MAX_LENGTH) add('firstName', 'FIELD_TOO_LONG', VOTER_MESSAGES.nameTooLong);
	if (middle.length > NAME_MAX_LENGTH) add('middleName', 'FIELD_TOO_LONG', VOTER_MESSAGES.nameTooLong);
	if (!last) add('lastName', 'MISSING_LAST_NAME', VOTER_MESSAGES.lastNameMissing);
	else if (last.length > NAME_MAX_LENGTH) add('lastName', 'FIELD_TOO_LONG', VOTER_MESSAGES.nameTooLong);

	const phone = text(input.phoneNumber);
	if (!phone) {
		if (!options.phoneOptional) add('phoneNumber', 'MISSING_PHONE', VOTER_MESSAGES.phoneMissing);
	} else if (!parseNgPhone(phone).ok) add('phoneNumber', 'INVALID_PHONE', VOTER_MESSAGES.phoneInvalid);

	const email = text(input.email);
	if (!email) {
		if (isFieldRequired('email', mode)) add('email', 'MISSING_EMAIL', VOTER_MESSAGES.emailMissing);
	} else if (!EMAIL_SHAPE.test(email)) add('email', 'INVALID_EMAIL', VOTER_MESSAGES.emailInvalid);

	const vin = normalizeVin(input.voterRegistrationNumber);
	if (!vin) {
		if (isFieldRequired('voterRegistrationNumber', mode))
			add('voterRegistrationNumber', 'MISSING_VIN', VOTER_MESSAGES.vinMissing);
	} else if (!VIN_SHAPE.test(vin)) add('voterRegistrationNumber', 'INVALID_VIN', VOTER_MESSAGES.vinInvalid);

	if (isFieldOffered('gender', mode) && normalizeGender(input.gender) === null) {
		add('gender', 'INVALID_GENDER', VOTER_MESSAGES.genderInvalid);
	}

	if (isFieldOffered('dateOfBirth', mode)) {
		const dob = normalizeDob(input.dateOfBirth);
		if (dob === null) add('dateOfBirth', 'INVALID_DATE_OF_BIRTH', VOTER_MESSAGES.dobInvalid);
		else if (dob) {
			const age = ageOn(dob, todayIso);
			if (age < VOTER_MIN_AGE) add('dateOfBirth', 'UNDER_18', VOTER_MESSAGES.underAge);
			else if (age > VOTER_MAX_AGE) add('dateOfBirth', 'INVALID_DATE_OF_BIRTH', VOTER_MESSAGES.dobTooOld);
		}
	}

	const pu = text(input.pollingUnitCode);
	if (!pu) add('pollingUnitCode', 'MISSING_POLLING_UNIT', VOTER_MESSAGES.puMissing);
	else {
		const parsed = normalizePuCode(pu);
		if (!parsed.ok) add('pollingUnitCode', 'INVALID_POLLING_UNIT_FORMAT', parsed.reason ?? 'Check the polling-unit code.');
	}

	return issues;
};

/** The first issue per field — what a form shows. */
export const issuesByField = (issues: readonly IVoterIssue[]): Partial<Record<VoterField, IVoterIssue>> => {
	const out: Partial<Record<VoterField, IVoterIssue>> = {};
	issues.forEach((issue) => {
		if (!out[issue.field]) out[issue.field] = issue;
	});
	return out;
};

// ── The form schema ──────────────────────────────────────────────────────────────────────────

/**
 * A yup schema that delegates every decision to `validateVoterInput`, so the form and the import can
 * never disagree. Each field's test runs the whole record and reports only its own issue.
 */
export const buildVoterYupSchema = (mode: VoterRulesMode, todayIso: string = lagosToday(), options: IValidateOptions = {}) => {
	const fieldTest = (field: VoterField) =>
		yup.string().test(field, function check() {
			const issues = validateVoterInput(this.parent as IVoterInput, mode, todayIso, options);
			const issue = issues.find((i) => i.field === field);
			return issue ? this.createError({ message: issue.message }) : true;
		});

	return VOTER_FIELD_RULES.reduce<Record<string, yup.StringSchema>>((schema, rule) => {
		schema[rule.field] = fieldTest(rule.field);
		return schema;
	}, {});
};

// ── Payload ──────────────────────────────────────────────────────────────────────────────────

export interface IVoterPayload {
	firstName: string;
	middleName: string;
	lastName: string;
	phoneNumber: string;
	email: string | null;
	voterRegistrationNumber: string | null;
	gender?: VoterGender | null;
	dateOfBirth?: string | null;
	pollingUnitCode: string;
}

/**
 * The normalised record the server receives. Phone goes as the 11-digit local form (`0803…`), the
 * form the register already stores; the PU code is canonical; optional blanks are `null`, not ''.
 * Baseline omits the two fields that server does not have, and keeps '' for the strings it
 * declares non-nullable.
 */
export const toVoterPayload = (input: IVoterInput, mode: VoterRulesMode): IVoterPayload => {
	const phone = parseNgPhone(text(input.phoneNumber));
	const pu = normalizePuCode(text(input.pollingUnitCode));
	const email = text(input.email);
	const vin = normalizeVin(input.voterRegistrationNumber);
	const base: IVoterPayload = {
		firstName: text(input.firstName),
		middleName: text(input.middleName),
		lastName: text(input.lastName),
		phoneNumber: phone.ok ? phone.local : text(input.phoneNumber),
		email: email || (mode === 'baseline' ? '' : null),
		voterRegistrationNumber: vin || (mode === 'baseline' ? '' : null),
		pollingUnitCode: pu.ok ? pu.code : text(input.pollingUnitCode),
	};
	if (mode === 'baseline') return base;
	const gender = normalizeGender(input.gender);
	const dob = normalizeDob(input.dateOfBirth);
	return { ...base, gender: gender || null, dateOfBirth: dob || null };
};

/** "Adaeze O." — how a voter is named anywhere a full name is more than the moment needs. */
export const shortName = (first: string, last: string): string => {
	const f = text(first);
	const l = text(last);
	if (!f) return l;
	return l ? `${f} ${l.charAt(0).toUpperCase()}.` : f;
};
